import { useDialogStore } from '@/shared/stores/dialogStore';
import type { ComponentType } from 'react';

/**
 * The dialog registry API — generic and feature-free so it lives in `shared/`.
 * Two layers:
 *  - Compile-time: the {@link DialogRegistry} interface maps a dialog id to its
 *    "own" props. It is empty here; each feature augments it via
 *    `declare module '@/shared/lib/dialogRegistry'` so `openDialog(id, props)` is
 *    type-checked per id.
 *  - Runtime: a module-level component map populated by {@link registerDialog}.
 *    App-level dialogs register at bootstrap (`src/app/dialogRegistry.tsx`);
 *    page-owned dialogs register from their owning route via feature hooks that
 *    compose `useRegisterDialog`. Registration always precedes opening, so the
 *    host finds the component at render time.
 */

/** The controlled contract every registered dialog satisfies (the host injects these). */
export type BaseDialogProps = { open: boolean; onOpenChange: (open: boolean) => void };

// biome-ignore lint/suspicious/noEmptyInterface: augmented by feature/app modules via declaration merging
export interface DialogRegistry {}

export type DialogId = keyof DialogRegistry;

/** The component type a given dialog id must satisfy: base props + that id's own props. */
export type DialogComponent<K extends DialogId> = ComponentType<
  BaseDialogProps & DialogRegistry[K]
>;

/** The type the host renders dialogs as — props erased to a bag (the typed boundary is `openDialog`). */
export type AnyDialogComponent = ComponentType<Record<string, unknown>>;

const components = new Map<string, AnyDialogComponent>();

/** Register a dialog component under its id. Idempotent (last registration wins). */
export function registerDialog<K extends DialogId>(id: K, component: DialogComponent<K>): void {
  components.set(id as string, component as unknown as AnyDialogComponent);
}

/** Look up a registered component (used by the host). */
export function getDialogComponent(id: string): AnyDialogComponent | undefined {
  return components.get(id);
}

/** Open a registered dialog by id with its typed props. Returns the instance id. */
export function openDialog<K extends DialogId>(id: K, props: DialogRegistry[K]): string {
  return useDialogStore.getState().push(id as string, props);
}

/** Programmatically close a dialog opened via {@link openDialog}. */
export function closeDialog(instanceId: string): void {
  useDialogStore.getState().hide(instanceId);
}
